import { InputErrors } from './input-errors';

/**
 * Access default errors for for mcontrols.
 */
export abstract class Errors {
  /**
   * Get standard defaults for form control errors.
   *
   * This covers the following validation errors:
   *
   * - email
   * - exactlength
   * - forbidden
   * - min/max
   * - minlength/maxlength
   * - pattern
   * - phone
   * - required/requiredTrue
   * - whitespace
   */
  public static get defaults(): InputErrors {
    return {
      email: 'Must be a valid email',
      exactlength: (error) => `Must have exact length of ${error.exactlength}`,
      forbidden: 'This value is not permitted',
      min: (error) => `Must be at least ${error.min}`,
      max: (error) => `Must be no more than ${error.max}`,
      minlength: (error) => `Must be at least length ${error.minlength}`,
      maxlength: (error) => `Must be at most length ${error.maxlength}`,
      pattern: 'This value is not valid',
      phone: 'Must be a valid phone number',
      required: `This field is required`,
      requiredTrue: `This field is required`,
    };
  }

  public static get otp(): InputErrors {
    return this.defaultsWith({
      'required': 'Code is required',
      'pattern': 'Code must be numbers only',
      'exactlength': (error) => `Must be ${error.exactlength} digits`,
      'retry': 'Incorrect code',
    });
  }

  /**
   * Combine multiple error fields, with later entries overwriting earlier ones in the case of any conflicts.
   *
   * @example
   * const otpErrors = Errors.combine(Errors.otp, {
   *   'foo': 'Some custom error',
   * });
   *
   * @example
   * const overwrite = Errors.combine({ foo: 'A', bar: 'C' }, { foo: 'B' }) // returns { foo: 'B', bar: 'C' }
   *
   * @param errors The layers of input errors to combine.
   * @returns The combined input errors.
   */
  public static combine(...errors: InputErrors[]): InputErrors {
    return Object.assign({ }, ...errors);
  }

  /**
   * Get an input errors object representing the standard defaults plus any additional error feedback.
   *
   * This is shorthand for `Errors.combine(Errors.defaults, { ... })`.
   *
   * @example
   * const sprocketNameErrors = Errors.defaultsWith({
   *   pattern: `This isn't a valid sprocket name.`, // override the original pattern error text
   *   fobbed: `This sprocket is fobbed and cannot be used.`, // add an error text
   * });
   *
   * @param errors Layers of errors to apply
   * @returns The combined defaults + your additional errors.
   */
  public static defaultsWith(...errors: InputErrors[]): InputErrors {
    return this.combine(this.defaults, ...errors);
  }
}
